import { formatPesoAmount } from '../utils/money';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiTrash2 } from "react-icons/fi";
import { getStockLists, createStockList, deleteStockList, getCategories, getBranchTaxes } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { resolveProviderTotalAmount, resolveStockCostAmount } from '../utils/stockCost';
import ProductCreationModal from '../components/ProductCreationModal';

const DEFAULT_ACTIVATION_HOURS = 18;

const formatDateTimeLocal = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (entry) => String(entry).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getDefaultActivationInput = () =>
  formatDateTimeLocal(new Date(Date.now() + DEFAULT_ACTIVATION_HOURS * 60 * 60 * 1000));

const formatActivationDate = (value) => {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString("es-CL") : "";
};

const StockListsView = () => {
  const [loading, setLoading] = useState(true);
  const [stockLists, setStockLists] = useState([]);
  const [pagination, setPagination] = useState(() => normalizePaginatedResponse(null));
  const [loadError, setLoadError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState({ page: 1, pageSize: 20, startDate: '', endDate: '', listType: 'all' });
  const [modalOpen, setModalOpen] = useState(false);
  const [newStockListName, setNewStockListName] = useState('');
  const [newStockListType, setNewStockListType] = useState('standard');
  const [newStockListUseTimer, setNewStockListUseTimer] = useState(false);
  const [newStockListActivationAt, setNewStockListActivationAt] = useState(() => getDefaultActivationInput());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesError, setTaxesError] = useState("");
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const categoryOptions = categories;
  const invalidDateRange = Boolean(query.startDate && query.endDate && query.startDate > query.endDate);
  const updateFilters = (changes) => setQuery((previous) => ({ ...previous, ...changes, page: 1 }));

  const canCreateStock = hasPermission(user, 'stock.create');
  const canDeleteStock = hasPermission(user, 'stock.delete');
  const canCreateProduct = hasPermission(user, 'product.create');
  const canUpdateProduct = hasPermission(user, 'product.update');
  const canReadTaxes = hasPermission(user, 'tax.read');

  const openCreateStockListModal = () => {
    setNewStockListUseTimer(false);
    setNewStockListActivationAt(getDefaultActivationInput());
    setModalOpen(true);
  };

  useEffect(() => {
    let active = true;
    const fetchStockLists = async () => {
      setLoadError('');
      if (!branchId || invalidDateRange) {
        setStockLists([]);
        setPagination(normalizePaginatedResponse(null));
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getStockLists(branchId, query.startDate || undefined, query.endDate || undefined, {
          page: query.page,
          pageSize: query.pageSize,
          ...(query.listType !== 'all' ? { listType: query.listType } : {}),
        });
        if (!active) return;
        const normalized = normalizePaginatedResponse(data);
        const lastPage = Math.max(1, normalized.totalPages);
        if (query.page > lastPage) {
          setQuery((previous) => ({ ...previous, page: lastPage }));
          return;
        }
        setStockLists(normalized.data);
        setPagination(normalized);
      } catch (error) {
        if (!active) return;
        setStockLists([]);
        setPagination(normalizePaginatedResponse(null));
        setLoadError('No se pudieron cargar las listas de compra.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchStockLists();
    return () => { active = false; };
  }, [branchId, query, refreshKey, invalidDateRange]);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const data = await getCategories({ branchId });
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        setCategoriesError("No se pudieron cargar las categorías.");
      } finally {
        setCategoriesLoading(false);
      }
    };
    if (branchId) {
      fetchCategories();
    }
  }, [branchId]);

  useEffect(() => {
    if (!branchId || !canReadTaxes) {
      setTaxes([]);
      setTaxesError(canReadTaxes ? '' : 'No tienes permisos para ver impuestos.');
      return;
    }
    setTaxesLoading(true);
    setTaxesError('');
    getBranchTaxes(branchId)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setTaxes(list.filter((tax) => tax?.branchActive !== false && tax?.isActive !== false));
      })
      .catch((error) => {
        console.error('Error fetching taxes:', error);
        setTaxes([]);
        setTaxesError('No se pudieron cargar los impuestos.');
      })
      .finally(() => setTaxesLoading(false));
  }, [branchId, canReadTaxes]);

  const handleCreateStockList = async () => {
    if (!newStockListName.trim()) {
      notify('Por favor ingresa un nombre válido.');
      return;
    }
    const payload = {
      name: newStockListName,
      listType: newStockListType,
    };
    if (newStockListUseTimer) {
      const activationDate = new Date(newStockListActivationAt);
      if (!Number.isFinite(activationDate.getTime())) {
        notify('Selecciona un horario de activación válido.');
        return;
      }
      payload.autoActivationScheduledAt = activationDate.toISOString();
    }
    await createStockList(branchId, payload);
    setNewStockListName('');
    setNewStockListType('standard');
    setNewStockListUseTimer(false);
    setNewStockListActivationAt(getDefaultActivationInput());
    setModalOpen(false);
    updateFilters({});
    setRefreshKey((previous) => previous + 1);
  };

  const handleDeleteStockList = async (id) => {
    const approved = await confirm({
      title: 'Eliminar lista',
      message: '¿Estás seguro de que deseas eliminar esta lista de compras?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    await deleteStockList(branchId, id);
    setRefreshKey((previous) => previous + 1);
  };

  const getListPaymentSummary = (stockList) => {
    const stocks = stockList?.Stocks || [];
    return stocks.reduce(
      (acc, stock) => {
        const total = resolveProviderTotalAmount(stock);
        const cost = resolveStockCostAmount(stock);
        const paid = stock.paidAmount !== undefined
          ? Number(stock.paidAmount || 0)
          : (stock.StockPayments || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
        acc.total += total;
        acc.cost += cost;
        acc.paid += paid;
        acc.balance += Math.max(total - paid, 0);
        return acc;
      },
      { total: 0, cost: 0, paid: 0, balance: 0 }
    );
  };

  return (
    <>
      {isModalOpen && canCreateProduct && (
        <ProductCreationModal
          branchId={branchId}
          onClose={setIsModalOpen}
          onProductCreated={(product) => notify(product?.message || 'Producto creado correctamente.')}
          canCreateProduct={canCreateProduct}
          categories={categoryOptions}
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          taxes={taxes}
          taxesLoading={taxesLoading}
          taxesError={taxesError}
          canReadTaxes={canReadTaxes}
          canUpdateProduct={canUpdateProduct}
        />
      )}
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Listas de Compra</h1>
          {canCreateStock && (
            <button
              onClick={openCreateStockListModal}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Nueva Lista
            </button>
          )}
        </div>

        {canCreateProduct && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 mb-4"
          >
            + Nuevo Producto
          </button>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { value: 'all', label: 'Todas' },
            { value: 'standard', label: 'Normales' },
            { value: 'importaciones', label: 'Importaciones' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => updateFilters({ listType: option.value })}
              aria-pressed={query.listType === option.value}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                query.listType === option.value
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <fieldset className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <legend className="px-1 text-sm font-semibold text-slate-700">Fecha de creación</legend>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm text-slate-700" htmlFor="stock-lists-start-date">
              Desde
              <input
                id="stock-lists-start-date"
                type="date"
                value={query.startDate}
                max={query.endDate || undefined}
                onChange={(event) => updateFilters({ startDate: event.target.value })}
                aria-invalid={invalidDateRange}
                aria-describedby={invalidDateRange ? 'stock-lists-date-error' : undefined}
                className="rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700" htmlFor="stock-lists-end-date">
              Hasta
              <input
                id="stock-lists-end-date"
                type="date"
                value={query.endDate}
                min={query.startDate || undefined}
                onChange={(event) => updateFilters({ endDate: event.target.value })}
                aria-invalid={invalidDateRange}
                aria-describedby={invalidDateRange ? 'stock-lists-date-error' : undefined}
                className="rounded-lg border border-slate-300 px-3 py-2"
              />
            </label>
            <button
              type="button"
              disabled={!query.startDate && !query.endDate}
              onClick={() => updateFilters({ startDate: '', endDate: '' })}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-40"
            >
              Limpiar fechas
            </button>
          </div>
          {invalidDateRange && (
            <p id="stock-lists-date-error" role="alert" className="mt-2 text-sm text-red-700">
              La fecha desde no puede ser posterior a la fecha hasta.
            </p>
          )}
        </fieldset>

        <label className="mb-4 flex items-center gap-2 text-sm text-slate-700" htmlFor="stock-lists-page-size">
          Listas por página
          <select
            id="stock-lists-page-size"
            value={query.pageSize}
            onChange={(event) => updateFilters({ pageSize: Number(event.target.value) })}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2"
          >
            {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>

        {/* Stock Lists */}
        {loading ? (
          <p role="status">
            <InlineLoading />
          </p>
        ) : loadError ? (
          <div role="alert" className="text-sm text-red-700">
            {loadError}{' '}
            <button type="button" onClick={() => setRefreshKey((previous) => previous + 1)} className="underline">
              Reintentar
            </button>
          </div>
        ) : invalidDateRange ? null : stockLists.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No hay listas de compra para los filtros seleccionados.
          </p>
        ) : (
          <ul className="space-y-4">
            {stockLists.map((stockList) => {
              const summary = getListPaymentSummary(stockList);
              const isImportList = (stockList.listType || 'standard') === 'importaciones';
              return (
                <li
                  key={stockList.id}
                  className="bg-white shadow rounded p-4 cursor-pointer hover:bg-gray-100 transition"
                  onClick={() => navigate(`/branches/${branchId}/stock/${stockList.id}`)} // Navigate to stock list detail
                >
                  <div className="flex items-center justify-between gap-3 my-2">
                    <p className="text-lg font-semibold">{stockList.name}</p>
                    {stockList.pendingFormatCount > 0 && <p className="text-sm font-semibold text-amber-800">{stockList.pendingFormatCount} formatos pendientes</p>}
                    <span
                      className={`shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${
                        isImportList ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {isImportList ? 'Importaciones' : 'Normal'}
                    </span>
                  </div>
                  <p className='text-sm text-gray-500 my-2'>{new Date(stockList.createdAt).toLocaleDateString()}</p>
                  {stockList.autoActivationScheduledAt && (
                    <p className="text-xs text-slate-500 mb-2">
                      Activación: {formatActivationDate(stockList.autoActivationScheduledAt)}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-700 mb-3">
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      Costo: ${formatPesoAmount(summary.cost)}
                    </span>
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      Adeudado: ${formatPesoAmount(summary.total)}
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      Pagado: ${formatPesoAmount(summary.paid)}
                    </span>
                    {summary.balance > 0 && (
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded">
                        Pendiente: ${formatPesoAmount(summary.balance)}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center w-full">
                    {canCreateStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/stock/${stockList.id}/create`);
                        }}
                        className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
                      >
                        + Compra
                      </button>
                    )}
                    {canCreateStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/branches/${branchId}/stock/${stockList.id}/create-expense`);
                        }}
                        className="bg-yellow-500 text-white px-4 py-1 rounded hover:bg-yellow-600"
                      >
                        + Gasto
                      </button>
                    )}
                    {canDeleteStock && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStockList(stockList.id);
                        }}
                        className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600 flex items-center justify-center"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {pagination.total > 0 && !loadError && !invalidDateRange && (
          <nav aria-label="Paginación de listas de compra" className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <button
              type="button"
              disabled={loading || query.page <= 1}
              onClick={() => setQuery((previous) => ({ ...previous, page: previous.page - 1 }))}
              className="rounded-lg border border-slate-300 px-3 py-2 disabled:opacity-40"
            >
              Anterior
            </button>
            <span aria-live="polite">Página {pagination.page} de {pagination.totalPages} · {pagination.total} listas</span>
            <button
              type="button"
              disabled={loading || !pagination.hasNext}
              onClick={() => setQuery((previous) => ({ ...previous, page: previous.page + 1 }))}
              className="rounded-lg border border-slate-300 px-3 py-2 disabled:opacity-40"
            >
              Siguiente
            </button>
          </nav>
        )}
      </div>

      {/* Modal for Creating Stock List */}
      {modalOpen && canCreateStock && (
        <div
          className="fixed inset-0 bg-slate-900/60 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
          >
            <div className="flex items-start justify-between gap-4 bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nueva lista
                </p>
                <h2 className="text-xl font-semibold text-slate-900">
                  Crear lista de compra
                </h2>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Cerrar"
                type="button"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <input
                type="text"
                value={newStockListName}
                onChange={(e) => setNewStockListName(e.target.value)}
                placeholder="Ingresa el nombre de la lista"
                className="border border-slate-300 rounded-lg p-2 w-full"
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de lista
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'standard', label: 'Normal' },
                    { value: 'importaciones', label: 'Importaciones' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setNewStockListType(option.value)}
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                        newStockListType === option.value
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Activación automática
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: false, label: 'Sin timer' },
                    { value: true, label: 'Con timer' },
                  ].map((option) => (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => setNewStockListUseTimer(option.value)}
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                        newStockListUseTimer === option.value
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {newStockListUseTimer && (
                  <input
                    type="datetime-local"
                    value={newStockListActivationAt}
                    onChange={(e) => setNewStockListActivationAt(e.target.value)}
                    className="mt-2 border border-slate-300 rounded-lg p-2 w-full"
                  />
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Sin timer, la activación se decide al finalizar la lista.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateStockList}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                type="button"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StockListsView;

export {
  formatActivationDate,
  formatDateTimeLocal,
  getDefaultActivationInput,
};
